import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  formatNumber,
  initial,
  mealByTime,
  localNow,
  type HomeSummary,
  type Meal,
} from '@jungles/shared';
import { useApi } from '../lib/hooks';
import { useUser } from '../app/session';
import {
  ErrorState,
  Header,
  JungleStrip,
  Loader,
  Screen,
  TabBar,
} from '../components/Chrome';
import { ChevronIcon, SparkIcon } from '../components/icons';
import { WorkoutPicker } from '../components/WorkoutPicker';
import { FoodPicker } from '../components/FoodPicker';
import { GymLogo } from '../components/GymLogo';

/** H1–H3 · Главная (SPEC §5). На главной нет списков лиг — только плашка. */

export function HomeScreen() {
  const user = useUser();
  const navigate = useNavigate();
  const { data, error, loading, reload } = useApi<HomeSummary>('/api/home');
  const [picker, setPicker] = useState<'workout' | 'food' | null>(null);

  if (loading) {
    return (
      <Screen tabs>
        <Header />
        <Loader />
        <TabBar />
      </Screen>
    );
  }

  if (error || !data) {
    return (
      <Screen tabs>
        <Header />
        <ErrorState message={error ?? 'Нет данных'} onRetry={reload} />
        <TabBar />
      </Screen>
    );
  }

  const { week, points, league, allTime, tip } = data;
  const defaultMeal: Meal = mealByTime(localNow(user.timezone).time);

  return (
    <Screen tabs>
      <Header />

      {tip ? (
        <section aria-label="Подсказка jungles AI" style={{ margin: '4px 0 0' }}>
          <div className="ai-ctx">
            <SparkIcon />
            {tip.context}
            <ChevronIcon size={12} />
          </div>
          <p className="ai-h">
            <span className="emo" aria-hidden="true">
              {tip.emoji}
            </span>
            {tip.title}
          </p>
        </section>
      ) : null}

      <div className="quick">
        <button className="qk" type="button" onClick={() => setPicker('workout')}>
          Тренировка
          <span className="pl" aria-hidden="true">
            +
          </span>
          <img src="/assets/ui/tab-workouts.webp" alt="" />
        </button>
        <button className="qk" type="button" onClick={() => setPicker('food')}>
          Питание
          <span className="pl" aria-hidden="true">
            +
          </span>
          <img src="/assets/ui/tab-food.webp" alt="" />
        </button>
      </div>

      <div className="me-flat">
        <span className="me-av" aria-hidden="true">
          {user.photoUrl ? <img src={user.photoUrl} alt="" /> : initial(user.name)}
        </span>
        <div className="nm">
          <b>{user.name.split(' ')[0]}</b>
          <span className="gym-inline">
            {user.homeGymName ? <GymLogo name={user.homeGymName} logoUrl={user.homeGymLogoUrl} size="small" /> : null}
            <span>{user.homeGymName ?? 'Без зала'}</span>
          </span>
        </div>
        <button
          className="me-pts"
          type="button"
          onClick={() => navigate('/arena')}
          style={{ background: 'none', border: 0, cursor: 'pointer' }}
        >
          <span className="me-n">
            {formatNumber(points.total)}
            <img className="pt-ic" src="/assets/ui/points-diamond-bicep.webp" alt="очков" />
          </span>
          {league ? (
            <span className="me-lg">
              <img src={league.icon} alt="" />
              {league.name} {league.rank ? <b>#{league.rank}</b> : null}
            </span>
          ) : null}
        </button>
      </div>

      <section className="hero" aria-label="Моя неделя">
        <div className="row">
          <h2>Моя неделя</h2>
          <span className="nt">{week.rangeLabel}</span>
        </div>

        <div className="wk">
          {week.days.map((day) => (
            <div
              key={day.date}
              className={`day${day.isToday ? ' today' : ''}${day.isFuture ? ' fut' : ''}`}
            >
              {day.label}
              <b>{day.dayOfMonth}</b>
              <span className="dots">
                <i className={day.hasWorkout ? 'dt t' : 'dt'} />
                <i className={day.hasFood ? 'dt food' : 'dt'} />
              </span>
            </div>
          ))}
        </div>

        <div className="goal">
          <div className="row">
            <Link className="goal-link" to="/workouts"><b>Тренировки</b><ChevronIcon size={14} /></Link>
            <span>
              <b>{week.workoutsDone}</b> из {week.workoutsGoal} в неделю
            </span>
          </div>
          <div
            className="saus t"
            role="img"
            aria-label={`${week.workoutsDone} из ${week.workoutsGoal} тренировок`}
          >
            {Array.from({ length: week.workoutsGoal }, (_, index) => (
              <span key={index} className={index < week.workoutsDone ? 'on' : undefined} />
            ))}
          </div>
        </div>

        <div className="goal">
          <div className="row">
            <Link className="goal-link" to="/nutrition"><b>Питание</b><ChevronIcon size={14} /></Link>
            <span>
              <b>{week.foodDays}</b> из {week.foodDaysGoal} дней
            </span>
          </div>
          <div
            className="saus food"
            role="img"
            aria-label={`${week.foodDays} из ${week.foodDaysGoal} дней с записью питания`}
          >
            {week.days.map((day) => (
              <span key={day.date} className={day.hasFood ? 'on' : undefined} />
            ))}
          </div>
        </div>

        <p className="nt" style={{ margin: '14px 0 0' }}>
          <button
            className="back"
            type="button"
            onClick={() => navigate('/profile')}
            style={{ minHeight: 'auto', padding: 0, textDecoration: 'underline' }}
          >
            Изменить цели
          </button>
        </p>
      </section>

      <div className="secl">За всё время</div>
      <div className="tiles">
        <div className="tile">
          <img src="/assets/ui/elephant.webp" alt="" />
          <small>Поднято</small>
          <b>{formatNumber(allTime.liftedKg)} кг</b>
          <span>{allTime.liftedEquivalent}</span>
        </div>
        <div className="tile">
          <img src="/assets/ui/shark.webp" alt="" />
          <small>Съедено</small>
          <b>{formatNumber(allTime.eatenKcal)} ккал</b>
          <span>{allTime.eatenEquivalent}</span>
        </div>
      </div>

      <JungleStrip />
      <TabBar />

      {picker === 'workout' ? <WorkoutPicker onClose={() => setPicker(null)} /> : null}
      {picker === 'food' ? (
        <FoodPicker meal={defaultMeal} date={data.today} onClose={() => setPicker(null)} />
      ) : null}
    </Screen>
  );
}
